/**
 * modelCascadeResolver.js — Feb 2026
 *
 * Single source of truth for turning "which capability?" into
 * `{ model_key, fallback_models[] }` used by every text/LLM endpoint
 * that supports the shared fallback contract.
 *
 * Selection + fallback rules (agreed with user, Feb 2026):
 *
 *   1. Primary = the exact model the user picked in AI Toolkit for the
 *      capability (or its aliases). If no pick, primary = highest-priority
 *      model in the capability's catalog.
 *
 *   2. Fallback chain is built by walking the capability's `aiCapabilityTiers`
 *      entry:
 *
 *      A. If the entry has tier buckets ({ premium, standard, economy, … })
 *         we detect which tier the user's PRIMARY model lives in and cascade
 *         accordingly:
 *
 *           Primary in PREMIUM  → premium → standard → economy
 *           Primary in STANDARD → standard → economy  → premium
 *           Primary in ECONOMY  → economy  → standard → premium
 *
 *         Within each tier, models are ordered by their catalog priority
 *         (first entry = highest priority).
 *
 *      B. If the entry is a flat array (no tier field) we walk it in
 *         catalog priority order.
 *
 *   3. After every catalog model has been tried, the final fallback is
 *      the literal string `'default'` — the admin backend's contract is to
 *      resolve `'default'` to its own last-resort model. This guarantees
 *      the request never dies just because our catalog is stale.
 *
 *   4. The primary model is removed from the fallback list to avoid the
 *      same model being tried twice in a row.
 *
 * Public API:
 *   buildCapabilityFallbackChain(capability, aliases?) → { modelKey, fallbackModels: [], source }
 */

/**
 * Read the persisted user model pick for a capability. Consults every
 * alias in order and returns the first non-empty value.
 */
const readUserPick = (aliases) => {
    let selected = {};
    try {
        selected = JSON.parse(localStorage.getItem('selectedAIModels') || '{}') || {};
    } catch { /* noop */ }
    for (const alias of aliases) {
        if (selected[alias]) return { pick: selected[alias], alias };
        const ciMatch = Object.keys(selected).find(k => k.toLowerCase().trim() === alias.toLowerCase().trim());
        if (ciMatch && selected[ciMatch]) return { pick: selected[ciMatch], alias: ciMatch };
    }
    return { pick: null, alias: null };
};

/**
 * Read the tier catalog entry for a capability. Consults every alias in
 * order and returns the first non-empty entry.
 */
const readCatalogEntry = (aliases) => {
    let tiers = {};
    try {
        tiers = JSON.parse(localStorage.getItem('aiCapabilityTiers') || '{}') || {};
    } catch { /* noop */ }
    for (const alias of aliases) {
        if (tiers[alias]) return { entry: tiers[alias], alias };
        const ciMatch = Object.keys(tiers).find(k => k.toLowerCase().trim() === alias.toLowerCase().trim());
        if (ciMatch && tiers[ciMatch]) return { entry: tiers[ciMatch], alias: ciMatch };
    }
    return { entry: null, alias: null };
};

/** Extract the stable key from a model catalog entry. */
const modelKeyOf = (m) => (m?.key || m?.id || m?.name || '').toString();

/**
 * Given a tiered catalog entry `{ premium: [...], standard: [...], … }`
 * find which tier a modelKey belongs to. Case-insensitive comparison.
 * Returns one of 'premium' | 'standard' | 'economy' | null.
 */
const findTierOfModel = (tieredEntry, modelKey) => {
    if (!tieredEntry || typeof tieredEntry !== 'object' || Array.isArray(tieredEntry)) return null;
    const needle = (modelKey || '').toString().toLowerCase();
    if (!needle) return null;
    for (const tier of Object.keys(tieredEntry)) {
        const bucket = tieredEntry[tier];
        if (!Array.isArray(bucket)) continue;
        for (const m of bucket) {
            if (modelKeyOf(m).toLowerCase() === needle) return tier;
        }
    }
    return null;
};

/** Cascade order given the tier the primary model lives in. */
const cascadeOrderForTier = (tier) => {
    switch (tier) {
        case 'premium':  return ['premium', 'standard', 'economy'];
        case 'standard': return ['standard', 'economy',  'premium'];
        case 'economy':  return ['economy',  'standard', 'premium'];
        default:         return ['premium', 'standard', 'economy'];
    }
};

/**
 * Flatten a tiered catalog entry into a single deduped priority list
 * following the supplied cascade order. Any tier keys not in the
 * supplied order (e.g. admin adds a new "pro" bucket) are appended at
 * the end in insertion order.
 */
const flattenTieredEntry = (tieredEntry, cascadeOrder) => {
    if (!tieredEntry || typeof tieredEntry !== 'object' || Array.isArray(tieredEntry)) return [];
    const seen = new Set();
    const out = [];
    const push = (bucket) => {
        if (!Array.isArray(bucket)) return;
        for (const m of bucket) {
            const key = modelKeyOf(m).toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(modelKeyOf(m));
        }
    };
    for (const tier of cascadeOrder) push(tieredEntry[tier]);
    // Include any admin-added tier we don't know about, at the end.
    for (const [tier, bucket] of Object.entries(tieredEntry)) {
        if (cascadeOrder.includes(tier)) continue;
        push(bucket);
    }
    return out;
};

/**
 * Flatten a flat catalog entry (array) into a deduped priority list.
 */
const flattenFlatEntry = (flatEntry) => {
    if (!Array.isArray(flatEntry)) return [];
    const seen = new Set();
    const out = [];
    for (const m of flatEntry) {
        const key = modelKeyOf(m).toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(modelKeyOf(m));
    }
    return out;
};

/**
 * Main resolver. Given a capability key (and optional alias list),
 * returns `{ modelKey, fallbackModels[], source }`.
 *
 * The `source` field is diagnostic:
 *   • 'user-tiered'    — user picked + catalog is tiered → tier-aware cascade
 *   • 'user-flat'      — user picked + catalog is flat  → priority walk
 *   • 'catalog-tiered' — no user pick; catalog primary = premium head
 *   • 'catalog-flat'   — no user pick; catalog primary = first flat entry
 *   • 'default-only'   — no user pick, no catalog → just 'default'
 */
export const buildCapabilityFallbackChain = (capability, aliases = null) => {
    const aliasList = (aliases && aliases.length > 0)
        ? [capability, ...aliases]
        : [capability];

    const { pick: userPick } = readUserPick(aliasList);
    const { entry: catalog } = readCatalogEntry(aliasList);

    const isTiered = catalog && !Array.isArray(catalog) && typeof catalog === 'object';
    const isFlat   = Array.isArray(catalog) && catalog.length > 0;

    let modelKey = null;
    let fallbackModels = [];
    let source = null;

    if (isTiered) {
        const primaryTier = userPick ? findTierOfModel(catalog, userPick) : 'premium';
        const order = cascadeOrderForTier(primaryTier || 'premium');
        const flat = flattenTieredEntry(catalog, order);
        modelKey = userPick || flat[0] || null;
        fallbackModels = flat.filter(k => k.toLowerCase() !== (modelKey || '').toString().toLowerCase());
        source = userPick ? 'user-tiered' : 'catalog-tiered';
    } else if (isFlat) {
        const flat = flattenFlatEntry(catalog);
        modelKey = userPick || flat[0] || null;
        fallbackModels = flat.filter(k => k.toLowerCase() !== (modelKey || '').toString().toLowerCase());
        source = userPick ? 'user-flat' : 'catalog-flat';
    } else if (userPick) {
        // User picked something but no catalog is available — send the
        // pick with only the 'default' safety-net.
        modelKey = userPick;
        fallbackModels = [];
        source = 'user-nocatalog';
    } else {
        modelKey = 'default';
        fallbackModels = [];
        source = 'default-only';
    }

    // Guaranteed backend-side safety net (agreed with user, answer 2a).
    // Strip any pre-existing 'default' entries from mid-chain (catalog
    // might list one) and always append it at the very end — unless the
    // primary itself is already 'default', in which case appending would
    // be redundant.
    fallbackModels = fallbackModels.filter(k => k !== 'default');
    if (modelKey !== 'default') fallbackModels.push('default');

    try {
        console.log('🧭 [modelCascade]', {
            capability,
            aliases: aliasList,
            modelKey,
            fallbackModels,
            source,
        });
    } catch { /* noop */ }

    return { modelKey, fallbackModels, source };
};
